import { json, redirect } from "@remix-run/node";
import { Link, useLoaderData, useParams } from "@remix-run/react";
import { SUPPORTED_LANGUAGES, isCourseFree, issuesCredential } from "@repo/shared";
import {
  ArrowLeft,
  BookOpen,
  Brain,
  ChevronDown,
  Clock,
  Code,
  FileText,
  Gift,
  Layers,
  HelpCircle,
  Lock,
  Play,
  Smartphone,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCourse, useCurrentUser } from "~/api/queries";
import { useOpenPreinscriptionCourses } from "~/api/queries/useOpenPreinscriptionCourses";
import { useUserDetails } from "~/api/queries/useUserDetails";
import qrCertificado from "~/assets/qr-certificado.png";
import DefaultPhotoCourse from "~/assets/svgs/default-photo-course.svg";
import { CoursePriceDisplay } from "~/components/CoursePriceDisplay/CoursePriceDisplay";
import { JsonLd } from "~/components/JsonLd";
import Viewer from "~/components/RichText/Viever";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CategoryChip } from "~/components/ui/CategoryChip";
import { Skeleton } from "~/components/ui/skeleton";
import { useRecentEnrollments } from "~/lib/stores/recentEnrollments";
import { cn, formatWithPlural } from "~/lib/utils";
import { CourseCardActions } from "~/modules/Cart/CourseCardActions";
import { ReserveInCartButton } from "~/modules/Cart/ReserveInCartButton";
import { AcademiaSeal } from "~/modules/Landing/components/CuadernoVivo/AcademiaSeal";
import { WashiTape } from "~/modules/Landing/components/CuadernoVivo/WashiTape";
import { sfx } from "~/modules/Landing/lib/landingSound";
import { credentialLabel, credentialLabelShort } from "~/types/certificate";
import {
  absoluteUrl,
  buildMeta,
  getCompanyFromMatches,
  truncateForMeta,
  ogImageUrl,
} from "~/utils/meta-helpers";
import { hasSession, serverFetchOutcome, serverFetchSafe } from "~/utils/server-fetch.server";

import { CheckMark } from "../components/CuadernoVivo/CheckMark";
import { leadSentences } from "../lib/excerpt";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import type { SupportedLanguages } from "@repo/shared";
import type { GetCourseResponse, LookupCourseResponse } from "~/api/generated-api";

const resolvePreferredLanguage = (): SupportedLanguages => SUPPORTED_LANGUAGES.ES;

/**
 * Loader de SERVIDOR. Reemplaza al `clientLoader` que había acá, que devolvía siempre
 * `{ course: null }` y existía sólo para tipar el `meta`.
 *
 * Qué gana el cambio:
 *  - El cuerpo de la página y el bloque JSON-LD `Course` salen en el HTML servido, que
 *    es lo único que ven GPTBot/ClaudeBot/PerplexityBot (no ejecutan JS).
 *  - `meta` pasa a ver el curso de verdad: título, description y OG dejan de ser genéricos.
 *  - La canonicalización de slug se resuelve como un 302 HTTP real en lugar de un
 *    redirect de cliente que un bot sin JS nunca llegaba a ejecutar.
 *
 * El fetch es el MISMO que ya hace `server.js` para el alternate `/courses/:slug.md`:
 * `API_INTERNAL_URL` (loopback al Nest, sin pasar por Caddy), anónimo, timeout de 3s.
 */
export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const idOrSlug = params.slug || "";
  const language = resolvePreferredLanguage();
  // `authenticated` viaja hasta el componente: con sesión NO se pintan los botones hasta
  // saber la matrícula del usuario. Ver `hasSession` en server-fetch.server.ts.
  const authenticated = hasSession(request);
  const empty = { course: null as GetCourseResponse["data"] | null, authenticated };

  if (!idOrSlug) return json(empty, { status: 404 });

  const lookup = await serverFetchSafe<LookupCourseResponse>(
    `/api/course/lookup?id=${encodeURIComponent(idOrSlug)}&language=${language}`,
  );

  // Tolerante a errores igual que antes: si el lookup falla (404 de un curso en
  // pre-inscripción linkeado por uuid, red caída) seguimos derecho — `serverFetchSafe`
  // devuelve null y `useCourse` resuelve por id/slug en el cliente lo mismo.
  if (lookup?.data.status === "redirect" && lookup.data.slug) {
    const url = new URL(request.url);
    throw redirect(`/courses/${lookup.data.slug}${url.search}`, 302);
  }

  // Con sesión, esta ficha no es el destino: la pantalla del curso dentro de la app
  // (`/course/:slug/details`) es la única que sabe qué puede hacer esa persona — empezar,
  // continuar, o mejorar la muestra a acceso completo. Vale para cualquier puerta de
  // entrada: un CTA de la home, un link compartido por WhatsApp, un resultado de Google.
  //
  // Se decide en el servidor y no en un efecto del cliente para que no haya un parpadeo de
  // la página de marketing antes del salto. Un crawler no manda cookie, así que sigue
  // recibiendo esta ficha entera, que es la que se indexa.
  //
  // `?vista=publica` saltea el salto: es la única forma que tiene alguien del equipo de ver
  // la ficha de marketing tal como la recibe un visitante sin tener que desloguearse.
  const previewPublicView = new URL(request.url).searchParams.get("vista") === "publica";

  if (authenticated && !previewPublicView) {
    const canonicalSlug = lookup?.data.slug || idOrSlug;
    const url = new URL(request.url);
    throw redirect(`/course/${canonicalSlug}/details${url.search}`, 302);
  }

  const outcome = await serverFetchOutcome<GetCourseResponse>(
    `/api/course?id=${encodeURIComponent(idOrSlug)}&language=${language}`,
  );

  // Sin curso: el status depende de POR QUÉ no hay curso, y la diferencia importa.
  //   · La API contestó 4xx → el curso no existe → 404 REAL, no el soft-404 que había
  //     (200 con el cartel de "no encontrado" adentro, que le dice al crawler que la URL
  //     es buena). `json(..., { status })` renderiza el componente igual —React Router
  //     toma el status más profundo distinto de 200—, así que el humano ve la misma
  //     pantalla de siempre y el bot recibe el status posta.
  //   · La API no contestó (timeout, 5xx, red) → 503. Un 404 acá le estaría diciendo a
  //     Google que el curso dejó de existir, por un blip de treinta segundos.
  if (!outcome.ok) {
    return json(empty, { status: outcome.reason === "not-found" ? 404 : 503 });
  }

  const course = outcome.data.data;

  // CANONICALIZACIÓN uuid → slug. El `lookup` de arriba sólo redirige cuando la API contesta
  // `status: "redirect"`, y para un uuid VIGENTE contesta `"found"`: por ese camino
  // `/courses/<uuid>` y `/courses/<slug>` servían las dos la página completa, con el mismo
  // título y el mismo JSON-LD. Mientras las landings no renderizaban nada eso no molestaba
  // —las dos estaban vacías—; ahora es contenido duplicado, y quien elige cuál indexar es
  // Google, no nosotros.
  //
  // Se arregla acá y no con un `<link rel="canonical">` por dos motivos: deja UNA sola URL
  // en vez de dos que se declaran hermanas, y no toca el set de meta tags de `buildMeta`,
  // que tiene que tener el MISMO tamaño en todas las rutas (ver el comentario ahí: contar
  // distinto ya rompió la hidratación del `<head>` una vez).
  //
  // 302 y no 301: un 301 lo cachea el browser para siempre, y si algún día se le cambia el
  // slug a un curso esa caché apunta a una URL muerta sin forma de invalidarla.
  if (course?.slug && course.slug !== idOrSlug) {
    const url = new URL(request.url);
    throw redirect(`/courses/${course.slug}${url.search}`, 302);
  }

  // Existe pero NO está publicado (draft/private). Misma guarda de visibilidad que el
  // alternate `.md`: este fetch es ANÓNIMO —es lo que ve un crawler—, así que no sembramos
  // nada. Pero NO es un 404: la URL existe y el usuario con permisos la sigue resolviendo
  // por React Query, exactamente como hasta hoy.
  if (!course || course.status !== "published") return json(empty);

  return json({ course, authenticated });
};

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const company = getCompanyFromMatches(matches);
  const course = data?.course;

  if (!course) {
    return buildMeta({ title: `Curso | ${company}` });
  }

  return buildMeta({
    title: `${course.title} | ${company}`,
    description: truncateForMeta(course.description),
    image: ogImageUrl(course.thumbnailUrl),
    type: "article",
  });
};

const lessonTypeIcons: Record<string, typeof FileText> = {
  content: FileText,
  quiz: HelpCircle,
  ai_mentor: Brain,
  embed: Code,
};

export default function CourseDetailPage() {
  const { t } = useTranslation();
  const { slug = "" } = useParams();

  const courseLanguage = resolvePreferredLanguage();

  const [openChapters, setOpenChapters] = useState<Set<string>>(new Set());

  // El loader de servidor NO reemplaza a React Query: la SIEMBRA. `ssrCourse` es lo que
  // pinta el servidor y lo que pinta el cliente en su PRIMER render (viene serializado en
  // `window.__remixContext`), así que los dos coinciden y no hay mismatch de hidratación.
  // Apenas React Query resuelve, `fetchedCourse` gana: es la que trae el estado por-usuario
  // (enrolled/purchased), porque el fetch del servidor es anónimo a propósito.
  const { course: ssrCourse, authenticated: ssrAuthenticated } = useLoaderData<typeof loader>();
  const { data: fetchedCourse, isLoading } = useCourse(slug, courseLanguage);
  const course = fetchedCourse ?? ssrCourse ?? undefined;
  const { data: currentUser } = useCurrentUser();
  const { data: openPreinscriptions } = useOpenPreinscriptionCourses();
  const { data: authorDetails } = useUserDetails(course?.authorId ?? "");

  const isLoggedIn = !!currentUser;

  const authorName = authorDetails
    ? `${authorDetails.firstName ?? ""} ${authorDetails.lastName ?? ""}`.trim() || "Unknown"
    : "Unknown";

  const { isRecentlyEnrolled, isRecentlyPurchased } = useRecentEnrollments();
  const courseId = course?.id ?? "";
  const isEnrolled = course?.enrolled || isRecentlyEnrolled(courseId);
  // Sin cast: el detalle ahora declara `purchased` en su schema. Antes la consulta lo
  // calculaba pero no lo publicaba, así que el cliente tipado no lo veía y había que forzarlo.
  const isPurchased = course?.purchased || isRecentlyPurchased(courseId);

  const toggleChapter = (chapterId: string) => {
    setOpenChapters((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
        sfx("droplet"); // cerrar: nota que baja
      } else {
        next.add(chapterId);
        sfx("bloom"); // abrir: revelación cálida
      }
      return next;
    });
  };

  // El skeleton sólo si NO hay nada que mostrar. Con data del loader arrancamos con la
  // página pintada y la query refresca por detrás.
  //
  // La excepción es el visitante CON sesión: la data del loader es anónima, así que trae
  // `enrolled`/`purchased` en falso para todo el mundo. Pintar con eso le muestra al alumno
  // que YA COMPRÓ el botón "Comprar" hasta que React Query resuelve quién es — y ese botón
  // lleva al checkout. Con sesión esperamos, que es exactamente lo que pasaba antes de que
  // esta página tuviera loader. El anónimo (crawler incluido) no espera nada: para él la
  // data del loader ES la verdad, y es el único que necesita el HTML servido completo.
  if (isLoading && (!course || ssrAuthenticated)) {
    return <CourseDetailSkeleton />;
  }

  if (!course) {
    return (
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-2xl font-bold text-landing-text">
            {t("landing.courseDetail.notFound")}
          </h1>
          <Button asChild className="mt-6">
            <Link to="/courses">{t("landing.courseDetail.backToCourses")}</Link>
          </Button>
        </div>
      </section>
    );
  }

  const chapterCount = course.chapters?.length ?? 0;
  const lessonTotal =
    course.chapters?.reduce((total, chapter) => total + (chapter.lessonCount ?? 0), 0) ?? 0;
  const hasFreeChapters = course.chapters?.some((ch) => ch.isFreemium) ?? false;
  const isFree = isCourseFree(course);

  // Toda promesa de credencial de esta ficha cuelga de acá. `credentialType` es el dato de
  // este curso en particular: puede no entregar nada, y cuando entrega hay que decir QUÉ
  // entrega —constancia o certificado— en vez de prometer "certificado" para todos.
  const credentialType = course.credentialType;
  const issuesCred = issuesCredential(credentialType);

  const preinscription = openPreinscriptions?.find((open) => open.id === course?.id);

  const renderEnrollmentButton = () => {
    // Curso en pre-inscripción (aún no lanzado): el sidebar vende la reserva del lugar
    // con el 80% asegurado — no hay "Comenzar curso" ni compra hasta el lanzamiento.
    if (preinscription && !isEnrolled && !isPurchased) {
      return (
        <div className="flex flex-col gap-3">
          {preinscription.isPreinscribed ? (
            <span className="inline-flex items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-landing-success bg-landing-success-light px-5 py-3 text-[14px] font-bold text-landing-success">
              ✓ Ya estás pre-inscripto: te avisamos al lanzar
            </span>
          ) : (
            <ReserveInCartButton course={preinscription} checkoutOnClick />
          )}
          <p className="text-center text-[12.5px] leading-snug text-landing-muted">
            {t("landing.launch.next.ctaBenefit")}
          </p>
        </div>
      );
    }
    return (
      <CourseCardActions
        course={{
          id: course.id,
          title: course.title,
          slug: slug ?? null,
          thumbnailUrl: course.thumbnailUrl ?? null,
          authorName,
          categoryName: course.category ?? null,
          priceInCents: course.priceInCents,
          mercadopagoPriceInCents: course.mercadopagoPriceInCents ?? 0,
          currency: course.currency,
          mercadopagoProductId: course.mercadopagoProductId ?? null,
          hasFreeChapters,
        }}
        isEnrolled={isEnrolled}
        isPurchased={isPurchased}
        variant="detail"
      />
    );
  };

  const canAccessLesson = (chapterIsFreemium: boolean) => {
    if (isPurchased) return true;
    if (chapterIsFreemium && isLoggedIn) return true;
    return false;
  };

  const getLessonHint = (chapterIsFreemium: boolean) => {
    if (isPurchased) return null;
    if (chapterIsFreemium && !isLoggedIn) return t("landing.courseDetail.signInToAccess");
    if (!chapterIsFreemium && isEnrolled) return t("landing.courseDetail.purchaseToUnlock");
    if (!chapterIsFreemium) return t("landing.courseDetail.enrollToUnlock");
    return null;
  };

  const plainDescription = (course.description || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const isPreinscriptionCourse = Boolean(preinscription && !isEnrolled && !isPurchased);
  const mobileCta = isPreinscriptionCourse
    ? { to: `/preinscripcion/${course.id}`, label: "Pre-inscribirme gratis" }
    : isFree && !isPurchased
      ? { to: `/courses/${slug}/inscripcion`, label: t("landing.courseDetail.enrollFree") }
      : null;

  return (
    <>
      <section className="landing-grain relative bg-landing-cream/70 py-6 pb-24 backdrop-blur-[1px] sm:py-9 lg:pb-14">
        {/* `description` pasa por `truncateForMeta`, que saca los tags: `course.description`
            es rich text de la base y arranca con `<p>`. Crudo, el schema salía con HTML
            adentro —`"description":"<p><strong>De la planilla…"`— justo en el campo que un
            LLM cita. El `<meta name="description">` de al lado ya lo limpiaba; esto no.
            El tope de 5000 es sólo un techo sano: acá no aplica el límite de 160 de las
            meta tags, pero tampoco tiene sentido serializar un curso entero.

            `image` va ABSOLUTA por `absoluteUrl`: schema.org pide URL absoluta y
            `thumbnailUrl` viene relativa (`/api/file/proxy/...`). El `og:image` de al lado
            ya se absolutizaba; este bloque servía una ruta que nadie externo puede resolver. */}
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "Course",
            name: course.title,
            description: truncateForMeta(course.description, 5000),
            provider: { "@type": "Organization", name: "AcademIA" },
            ...(course.thumbnailUrl && { image: absoluteUrl(course.thumbnailUrl) }),
            ...(course.category && { about: { "@type": "Thing", name: course.category } }),
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/courses"
            data-cuelume-hover="tick"
            data-cuelume-press
            className="mb-5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-landing-muted transition-colors hover:text-landing-red"
          >
            <ArrowLeft className="size-3.5" />
            {t("landing.courseDetail.backToCourses")}
          </Link>

          {/* ═══ HERO compuesto: la promesa a la izquierda, el objeto a la derecha ═══ */}
          <div className="grid items-start gap-7 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CategoryChip category={course.category} />
                <span aria-hidden="true" className="text-landing-light">
                  ·
                </span>
                <span className="text-[13px] font-semibold text-landing-muted">
                  {chapterCount === 1
                    ? t("landing.courses.card.chapter")
                    : t("landing.courses.card.chapters", { count: chapterCount })}
                </span>
                {isFree && !isPurchased && (
                  <span className="rounded-full bg-landing-red px-3 py-1 text-[12px] font-extrabold uppercase tracking-[0.1em] text-white shadow-landing-red">
                    {t("landing.courseDetail.freeCourse")}
                  </span>
                )}
                {isPreinscriptionCourse && (
                  <span className="rotate-[-2deg] rounded-[5px] border-2 border-landing-warning bg-landing-white/70 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-landing-warning">
                    {t("landing.launch.next.prepStamp")}
                  </span>
                )}
                {!isFree && hasFreeChapters && !isEnrolled && (
                  <span className="rounded-full border border-landing-red/30 bg-landing-red-50 px-3 py-1 text-[12px] font-bold text-landing-red">
                    {t("landing.courseDetail.freeChapters")}
                  </span>
                )}
                {(isEnrolled || isPurchased) && (
                  <span className="rounded-full border border-landing-success/40 bg-landing-success-light px-3 py-1 text-[12px] font-bold text-landing-success">
                    ✓ {t("landing.courseDetail.enrolled")}
                  </span>
                )}
              </div>

              <h1
                className="font-display mt-4 text-[clamp(30px,4.4vw,46px)] font-extrabold leading-[1.08] tracking-tight text-landing-text"
                style={{ textWrap: "balance" }}
              >
                {course.title}
              </h1>

              {plainDescription && (
                <p className="mt-3 max-w-[52ch] text-[16px] leading-relaxed text-landing-muted">
                  {leadSentences(plainDescription, 165)}
                </p>
              )}

              {/* Cómo se estudia. Mismos chips con check SVG que el hero de la home: antes
                acá eran glifos ☑ chicos y grises, un estándar distinto por página. */}
              <ul className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
                {[
                  "Desde el celular o la computadora",
                  "A tu ritmo",
                  "En español",
                  "Sin saber de computación",
                ].map((item) => (
                  <li
                    key={item}
                    className="inline-flex items-center gap-2 rounded-full border border-landing-border bg-landing-paper/70 px-3 py-2 text-[13.5px] font-semibold leading-tight text-landing-text"
                  >
                    <CheckMark />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* El objeto: teléfono con video para el curso gratis, póster para el resto */}
            <div className="relative mx-auto w-full max-w-[440px]">
              {isFree && !isPurchased ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-[min(200px,54vw)] overflow-hidden rounded-[26px] border-[6px] border-[#2c2622] bg-[#14100e] shadow-landing-xl [aspect-ratio:9/17]">
                    <span
                      aria-hidden="true"
                      className="absolute left-1/2 top-2.5 z-10 h-[5px] w-[58px] -translate-x-1/2 rounded-full bg-[#3b332d]"
                    />
                    <video
                      className="h-full w-full object-cover"
                      src="/promo/promo-academia-15s.mp4"
                      poster={course.thumbnailUrl ?? undefined}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      aria-label={t("landing.launch.free.phoneCap")}
                    />
                    <span className="absolute bottom-3 left-0 right-0 z-10 text-center text-[10.5px] uppercase tracking-[0.08em] text-[#e8ded2] [text-shadow:0_1px_4px_rgba(0,0,0,.8)]">
                      {t("landing.launch.free.phoneCap")}
                    </span>
                  </div>
                  <span className="landing-hand rotate-[-2deg] text-[16px] text-landing-teal">
                    {t("landing.launch.free.phoneNote")} ↑
                  </span>
                </div>
              ) : (
                <div className="relative">
                  <WashiTape variant={4} className="!left-[12%]" rotate={-6} />
                  <div className="relative aspect-video w-full overflow-hidden rounded-[14px] bg-landing-ink-950 shadow-landing-xl">
                    <img
                      src={course.thumbnailUrl || DefaultPhotoCourse}
                      alt={course.title}
                      className={cn(
                        "h-full w-full object-cover",
                        isPreinscriptionCourse && "grayscale-[0.25]",
                      )}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DefaultPhotoCourse;
                      }}
                    />
                  </div>
                  {isPreinscriptionCourse && (
                    <span className="landing-hand absolute -bottom-5 right-[6%] rotate-[-2deg] text-[16px] text-landing-warning">
                      este curso se está cocinando ✍️
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ═══ Cuerpo: contenido + sidebar ═══ */}
          <div className="mt-12 grid gap-8 lg:mt-16 lg:grid-cols-[1fr_400px] lg:gap-12">
            <div className="flex min-w-0 flex-col gap-8">
              {/* Sobre este curso — hoja de papel */}
              <div className="relative min-w-0 rounded-[4px] bg-landing-paper p-6 shadow-landing-md sm:p-7">
                <WashiTape variant={6} className="!left-auto !right-[10%]" rotate={4} />
                <span className="landing-eyebrow mb-3 block font-bold text-landing-red">
                  {t("landing.courseDetail.aboutCourse")}
                </span>
                <Viewer
                  content={course.description || ""}
                  className="prose prose-neutral max-w-none break-words overflow-x-auto"
                  variant="content"
                />
              </div>

              {/* Temario — el recorrido del cuaderno */}
              {course.chapters && course.chapters.length > 0 && (
                <div>
                  <span className="landing-eyebrow block font-bold text-landing-teal">
                    {t("landing.courseDetail.chaptersCount", { count: chapterCount })}
                  </span>
                  <h2 className="font-display mb-5 mt-1 text-[24px] font-bold text-landing-text">
                    {t("landing.courseDetail.courseContent")}
                  </h2>
                  <div className="relative">
                    {/* El riel del camino: une las estaciones (capítulos) */}
                    <span
                      aria-hidden="true"
                      className="absolute bottom-8 left-[35px] top-8 w-[2.5px] rounded-full bg-landing-red/30"
                    />
                    <div className="flex flex-col gap-3">
                      {course.chapters.map((chapter, index) => {
                        const isOpen = openChapters.has(chapter.id);
                        const chapterIsFreemium = !!chapter.isFreemium;
                        const lessons = chapter.lessons ?? [];
                        const isStart = index === 0 && !isEnrolled && !isPurchased;

                        return (
                          <div
                            key={chapter.id}
                            className="relative overflow-hidden rounded-[14px] border-[1.5px] border-landing-border bg-landing-paper shadow-landing-sm transition-shadow hover:shadow-landing-md"
                          >
                            <button
                              type="button"
                              onClick={() => toggleChapter(chapter.id)}
                              className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-landing-surface"
                            >
                              <div className="relative shrink-0">
                                <div className="font-display flex size-11 items-center justify-center rounded-full border-[2.5px] border-landing-red bg-landing-white text-[18px] font-bold text-landing-red">
                                  {index + 1}
                                </div>
                                {isStart && (
                                  <span className="landing-hand absolute -top-2 left-12 hidden whitespace-nowrap rotate-[-3deg] text-[15px] leading-none text-landing-red sm:block">
                                    empezás acá ↓
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-display truncate text-[17px] font-semibold text-landing-text">
                                  {chapter.title}
                                </p>
                                <p className="text-sm text-landing-muted">
                                  {formatWithPlural(
                                    chapter.lessonCount,
                                    t("courseChapterView.other.lesson"),
                                    t("courseChapterView.other.lessons"),
                                  )}
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                {chapterIsFreemium ? (
                                  <Badge className="border-landing-red-200 bg-landing-red-50 text-xs text-landing-red">
                                    {t("landing.courses.card.free")}
                                  </Badge>
                                ) : (
                                  !isPurchased && <Lock className="size-4 text-landing-light" />
                                )}
                                <ChevronDown
                                  className={cn(
                                    "size-5 text-landing-teal transition-transform",
                                    isOpen && "rotate-180",
                                  )}
                                />
                              </div>
                            </button>

                            {isOpen && lessons.length > 0 && (
                              <div className="border-t border-landing-border/70 bg-landing-surface">
                                {lessons.map((lesson) => {
                                  const LessonIcon = lessonTypeIcons[lesson.type] ?? FileText;
                                  const accessible = canAccessLesson(chapterIsFreemium);
                                  const hint = getLessonHint(chapterIsFreemium);
                                  const lessonUrl = `/course/${slug}/lesson/${lesson.id}`;

                                  const content = (
                                    <div
                                      className={cn(
                                        "flex items-center gap-3 px-4 py-3 pl-[4.7rem]",
                                        accessible
                                          ? "transition-colors hover:bg-landing-cream/60"
                                          : "opacity-60",
                                      )}
                                    >
                                      <LessonIcon className="size-4 shrink-0 text-landing-teal" />
                                      <div className="min-w-0 flex-1">
                                        <p
                                          className={cn(
                                            "truncate text-sm",
                                            accessible ? "text-landing-text" : "text-landing-muted",
                                          )}
                                        >
                                          {lesson.title}
                                        </p>
                                        <p className="text-xs text-landing-light">
                                          {t(`landing.courseDetail.lessonTypes.${lesson.type}`)}
                                        </p>
                                      </div>
                                      {accessible ? (
                                        <Play className="size-3.5 shrink-0 text-landing-red" />
                                      ) : hint ? (
                                        <span className="hidden shrink-0 text-xs text-landing-light sm:inline">
                                          {hint}
                                        </span>
                                      ) : null}
                                    </div>
                                  );

                                  if (accessible) {
                                    return (
                                      <Link
                                        key={lesson.id}
                                        to={lessonUrl}
                                        data-cuelume-hover="tick"
                                        data-cuelume-press
                                        className="block"
                                      >
                                        {content}
                                      </Link>
                                    );
                                  }

                                  if (chapterIsFreemium && !isLoggedIn) {
                                    return (
                                      <Link
                                        key={lesson.id}
                                        to={`/auth/login?redirect=${encodeURIComponent(`/courses/${slug}`)}`}
                                        data-cuelume-hover="tick"
                                        data-cuelume-press
                                        className="block"
                                      >
                                        {content}
                                      </Link>
                                    );
                                  }

                                  return <div key={lesson.id}>{content}</div>;
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Meta final del camino. Se muestra SÓLO si este curso emite
                          credencial, y la nombra por lo que es: los gratis entregan
                          constancia y los pagos certificado. Antes prometía "certificado"
                          en todos los cursos, incluso en los que no entregan nada. */}
                      {issuesCred && (
                        <div className="relative flex items-center gap-4 pl-1">
                          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-landing-success text-[15px] font-extrabold text-white shadow-landing-sm">
                            ✓
                          </div>
                          <p className="font-display text-[16px] italic text-landing-text">
                            Al terminar: tu {credentialLabelShort(credentialType)} verificable, con
                            código propio.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* La credencial de ESTE curso — realista, con sello y QR verificable.
                  Va SÓLO si el curso la emite: era la promesa más fuerte de la página
                  (una maqueta con QR se lee como un hecho, no como un ejemplo) y se
                  pintaba en la ficha de todos los cursos, sin mirar el dato. */}
              {issuesCred && (
                <div className="relative mx-auto w-full max-w-[520px] rotate-[0.6deg] rounded-[6px] border-2 border-landing-border bg-landing-paper p-7 shadow-landing-lg sm:p-8">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-[8px] rounded-[3px] border border-landing-red/35"
                  />
                  <AcademiaSeal size={56} className="absolute -top-3.5 right-5 rotate-[8deg]" />
                  <p className="font-display text-[15px] font-bold text-landing-text">
                    Academ<span className="text-landing-red">IA</span>
                  </p>
                  {/* El rótulo de la credencial va acá abajo y no arriba a la derecha:
                      ese rincón lo ocupa el sello. */}
                  <p className="mt-1 text-[9.5px] font-bold uppercase tracking-[0.2em] text-landing-teal">
                    {credentialLabel(credentialType)}
                  </p>
                  <p className="mt-3 text-[9.5px] uppercase tracking-[0.2em] text-landing-muted">
                    {t("landing.launch.cert.example.certifies")}
                  </p>
                  <p className="landing-hand mt-0.5 text-[24px] leading-none text-landing-text">
                    tu nombre, acá
                  </p>
                  <p className="mt-2.5 text-[9.5px] uppercase tracking-[0.2em] text-landing-muted">
                    {t("landing.launch.cert.example.completed")}
                  </p>
                  <p className="font-display mt-0.5 text-[16px] text-landing-text">
                    {course.title}
                  </p>
                  <div className="mt-5 flex items-end justify-between gap-4">
                    <p className="text-[10.5px] leading-relaxed text-landing-muted [font-variant-numeric:tabular-nums]">
                      {t("landing.launch.cert.example.token")}:{" "}
                      <b className="text-landing-teal">#····</b>
                      <br />
                      {t("landing.launch.cert.example.verifyAt")}
                    </p>
                    <Link
                      to="/certificado"
                      aria-label="Ver un ejemplo de credencial verificable"
                      data-cuelume-hover="tick"
                      data-cuelume-press
                      className="block shrink-0 transition-transform hover:scale-105"
                    >
                      <img
                        src={qrCertificado}
                        alt="Código QR de verificación"
                        width={56}
                        height={56}
                        className="h-[56px] w-[56px] rounded-[6px] border-[3px] border-white shadow-[0_0_0_1.5px_#E5DFD3]"
                      />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar — la ficha del curso: estado + acción + qué incluye */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="relative rounded-[16px] border-[1.5px] border-landing-border bg-landing-paper p-6 shadow-landing-lg">
                <WashiTape variant={7} />
                {isPreinscriptionCourse ? (
                  <div className="mb-5">
                    <span className="inline-block rotate-[-2deg] rounded-[5px] border-2 border-landing-warning bg-landing-white/70 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-landing-warning">
                      {t("landing.launch.next.prepStamp")}
                    </span>
                    <p className="mt-3 text-[14px] leading-relaxed text-landing-text">
                      Este curso todavía no se lanzó. Pre-inscribite gratis y te avisamos por
                      WhatsApp apenas esté listo.
                    </p>
                  </div>
                ) : (
                  <div className="mb-6">
                    <CoursePriceDisplay
                      priceInCents={course.priceInCents}
                      mercadopagoPriceInCents={course.mercadopagoPriceInCents}
                      mercadopagoProductId={course.mercadopagoProductId}
                      variant="detail"
                    />
                  </div>
                )}
                <div className="space-y-3">{renderEnrollmentButton()}</div>
                <div className="mt-6 border-t-[1.5px] border-dashed border-landing-border pt-5">
                  <span className="landing-eyebrow mb-3 block font-bold text-landing-teal">
                    Este curso incluye
                  </span>
                  <ul className="space-y-3 text-sm text-landing-text">
                    <li className="flex items-center gap-3">
                      <BookOpen className="size-5 shrink-0 text-landing-teal" />
                      <span>
                        {chapterCount === 1
                          ? t("landing.courses.card.chapter")
                          : t("landing.courses.card.chapters", { count: chapterCount })}
                        {lessonTotal > 0 && (
                          <>
                            {" · "}
                            {formatWithPlural(
                              lessonTotal,
                              t("courseChapterView.other.lesson"),
                              t("courseChapterView.other.lessons"),
                            )}
                          </>
                        )}
                      </span>
                    </li>
                    {/* Se sacó "Mentor IA para preguntar cuando te trabás": el mentor es UNA
                        clase del curso, no un acompañamiento permanente, y además "trabás"
                        es una forma que no se usa fuera del Río de la Plata. */}
                    <li className="flex items-center gap-3">
                      <Layers className="size-5 shrink-0 text-landing-teal" />
                      <span>Clases cortas, para avanzar de a poco</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Smartphone className="size-5 shrink-0 text-landing-teal" />
                      <span>Se ve desde el celular o la computadora</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Clock className="size-5 shrink-0 text-landing-teal" />
                      <span>A tu ritmo, sin horarios ni fechas de entrega</span>
                    </li>
                    {/* Sólo si este curso emite credencial, y con el nombre de la que
                        emite. La clave de i18n que había acá dice "certificado" fijo, que
                        es falso en los cursos gratis: esos entregan constancia. */}
                    {issuesCred && (
                      <li className="flex items-center gap-3">
                        <span aria-hidden="true" className="w-5 text-center text-base leading-none">
                          📜
                        </span>
                        <span className="text-landing-teal">
                          Con {credentialLabelShort(credentialType)} verificable al terminar
                        </span>
                      </li>
                    )}
                    {hasFreeChapters && (
                      <li className="flex items-center gap-3">
                        <Gift className="size-5 shrink-0 text-landing-red" />
                        <span>{t("landing.courseDetail.freeChapters")}</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA sticky en mobile: la acción principal siempre a un pulgar de distancia.
          `data-bottom-bar` lo lee FloatingActions para levantarse por encima — si no, el
          botón de WhatsApp le tapa el CTA de inscripción. Tiene que vivir FUERA de la
          sección con backdrop-filter: un ancestro filtrado convierte `position: fixed`
          en relativo a esa sección y mandaba la barra al final de la página. */}
      {mobileCta && (
        <div
          data-bottom-bar
          className="fixed inset-x-0 bottom-0 z-40 border-t-[1.5px] border-landing-border bg-landing-paper/95 px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_30px_rgba(31,26,21,0.12)] backdrop-blur lg:hidden"
        >
          <div className="mx-auto flex max-w-md items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-landing-text">{course.title}</p>
              <p className="text-[12px] text-landing-muted">
                {isPreinscriptionCourse ? "−80% asegurado al lanzar" : "Gratis · con tu WhatsApp"}
              </p>
            </div>
            <Link
              to={mobileCta.to}
              data-cuelume-press
              data-cuelume-release
              className="shrink-0 rounded-[10px] bg-landing-red px-5 py-3 text-[14px] font-bold text-white shadow-landing-red"
            >
              {mobileCta.label}
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

function CourseDetailSkeleton() {
  return (
    <section className="py-12 sm:py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Skeleton className="h-5 w-32 mb-8" />
        <div className="grid gap-8 lg:grid-cols-[1fr_400px] lg:gap-12">
          <div className="flex flex-col gap-6">
            <Skeleton className="aspect-video w-full rounded-xl" />
            <div className="flex flex-col gap-4">
              <div className="flex gap-2">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-20" />
              </div>
              <Skeleton className="h-10 w-3/4" />
              <div className="flex items-center gap-3">
                <Skeleton className="size-10 rounded-full" />
                <div>
                  <Skeleton className="h-4 w-16 mb-1" />
                  <Skeleton className="h-5 w-32" />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Skeleton className="h-7 w-48 mb-4" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
          <div>
            <div className="rounded-xl border border-neutral-200 bg-landing-white p-6">
              <Skeleton className="h-9 w-24 mb-6" />
              <Skeleton className="h-12 w-full mb-3" />
              <div className="mt-6 space-y-3 border-t border-neutral-100 pt-6">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-40" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
